import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronDown, ChevronRight, Plus, Send, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  createOrganisation,
  decommissionProperty,
  deleteOrganisation,
  getOrganisations,
  adminInvitePropertyManager,
  listOrgProperties,
  provisionEnterpriseCustomer,
  provisionEstateCustomer,
  reactivateProperty,
  reinstateOrganisation,
  resendActivation,
  suspendOrganisation,
  updateOrganisation,
} from '@/services/api.client';
import { extractApiErrorMessage } from '@/lib/api-error';
import type { OrgProperty } from '@/types';
import type { Organisation } from '@/types';

// ── Create Org form ───────────────────────────────────────────────────────────

const createSchema = z.object({
  name: z.string().min(2, 'Name required'),
  type: z.enum(['estate_management', 'security_company']),
  contactName: z.string().min(2, 'Contact name required'),
  contactEmail: z.string().email('Valid email required'),
});
type CreateForm = z.infer<typeof createSchema>;

function CreateOrgDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateForm>({ resolver: zodResolver(createSchema) });
  const [submitError, setSubmitError] = useState('');

  const mutation = useMutation({
    mutationFn: createOrganisation,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'organisations'] });
      reset();
      onClose();
    },
    onError: (err: unknown) =>
      setSubmitError(extractApiErrorMessage(err, 'Failed to create customer. Please try again.')),
  });

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      reset();
      setSubmitError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Create Customer</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((data) => {
            setSubmitError('');
            mutation.mutate(data);
          })}
          className="space-y-4"
        >
          <div className="space-y-1">
            <Label htmlFor="org-name">Customer Name</Label>
            <Input id="org-name" {...register('name')} placeholder="Acme Properties Ltd" />
            {errors.name && (
              <p className="text-xs text-[var(--status-red-text)]">{errors.name.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="org-type">Type</Label>
            <select
              id="org-type"
              {...register('type')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="estate_management">Estate Management</option>
              <option value="security_company">Security Company</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="contact-name">Contact Name</Label>
            <Input id="contact-name" {...register('contactName')} placeholder="Jane Wanjiru" />
            {errors.contactName && (
              <p className="text-xs text-[var(--status-red-text)]">{errors.contactName.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="contact-email">Contact Email</Label>
            <Input
              id="contact-email"
              type="email"
              {...register('contactEmail')}
              placeholder="jane@acme.co.ke"
            />
            {errors.contactEmail && (
              <p className="text-xs text-[var(--status-red-text)]">{errors.contactEmail.message}</p>
            )}
          </div>
          {submitError && <p className="text-xs text-[var(--status-red-text)]">{submitError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create Customer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Suspend confirmation ──────────────────────────────────────────────────────

function SuspendConfirmDialog({ org, onClose }: { org: Organisation | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [confirmValue, setConfirmValue] = useState('');
  const [actionError, setActionError] = useState('');

  const mutation = useMutation({
    mutationFn: (name: string) => suspendOrganisation(org?.id ?? '', name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'organisations'] });
      setConfirmValue('');
      onClose();
    },
    onError: () => setActionError('Failed to suspend. Please try again.'),
  });

  if (!org) return null;

  return (
    <Dialog
      open={!!org}
      onOpenChange={() => {
        setConfirmValue('');
        setActionError('');
        onClose();
      }}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Suspend Customer</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[var(--text-secondary)]">
            This will suspend all users and invalidate all sessions for <strong>{org.name}</strong>.
            Type the customer name to confirm.
          </p>
          <div className="space-y-1">
            <Label htmlFor="confirm-name">Confirm customer name</Label>
            <Input
              id="confirm-name"
              value={confirmValue}
              onChange={(e) => setConfirmValue(e.target.value)}
              placeholder={org.name}
            />
          </div>
          {actionError && <p className="text-xs text-[var(--status-red-text)]">{actionError}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={confirmValue !== org.name || mutation.isPending}
            onClick={() => mutation.mutate(confirmValue)}
          >
            {mutation.isPending ? 'Suspending…' : 'Suspend Customer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Purge (permanent delete) confirmation ────────────────────────────────────

function PurgeConfirmDialog({ org, onClose }: { org: Organisation | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [confirmValue, setConfirmValue] = useState('');
  const [actionError, setActionError] = useState('');

  const mutation = useMutation({
    mutationFn: (name: string) => deleteOrganisation(org?.id ?? '', name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'organisations'] });
      setConfirmValue('');
      onClose();
    },
    onError: (err: unknown) =>
      setActionError(
        extractApiErrorMessage(
          err,
          'Failed to delete. Make sure the organisation is suspended and has no access events.',
        ),
      ),
  });

  if (!org) return null;

  return (
    <Dialog
      open={!!org}
      onOpenChange={() => {
        setConfirmValue('');
        setActionError('');
        onClose();
      }}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Permanently Delete Customer</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            This is irreversible. All properties, units, gates, users, and configuration for{' '}
            <strong>{org.name}</strong> will be permanently deleted. Only allowed when the
            organisation is suspended and has no recorded access events.
          </div>
          <p className="text-sm text-[var(--text-secondary)]">Type the customer name to confirm.</p>
          <div className="space-y-1">
            <Label htmlFor="purge-confirm-name">Confirm customer name</Label>
            <Input
              id="purge-confirm-name"
              value={confirmValue}
              onChange={(e) => setConfirmValue(e.target.value)}
              placeholder={org.name}
            />
          </div>
          {actionError && <p className="text-xs text-[var(--status-red-text)]">{actionError}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={confirmValue !== org.name || mutation.isPending}
            onClick={() => mutation.mutate(confirmValue)}
          >
            {mutation.isPending ? 'Deleting…' : 'Delete Permanently'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Invite Customer (single-tenant enterprise, ADR 010) ──────────────────────

const inviteCustomerSchema = z.object({
  fullName: z.string().min(2, 'Contact name required'),
  email: z.string().email('Valid email required'),
  companyName: z.string().min(2, 'Company name required'),
  buildingAddress: z.string().min(2, 'Building address required'),
  subdomain: z
    .string()
    .regex(
      /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/,
      'Lowercase letters, digits, hyphens; 3–40 chars',
    )
    .optional()
    .or(z.literal('')),
});
type InviteCustomerForm = z.infer<typeof inviteCustomerSchema>;

function InviteCustomerDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteCustomerForm>({ resolver: zodResolver(inviteCustomerSchema) });
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const mutation = useMutation({
    mutationFn: provisionEnterpriseCustomer,
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'organisations'] });
      setSuccessMessage(
        `Activation email sent to ${result.email}. Subdomain: ${result.subdomain}.`,
      );
      reset();
    },
    onError: (err: unknown) => {
      setSubmitError(extractApiErrorMessage(err, 'Failed to invite customer. Please try again.'));
    },
  });

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      reset();
      setSubmitError('');
      setSuccessMessage('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Invite a Customer</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[var(--text-secondary)]">
          Stand up a single-tenant enterprise customer in one step. The customer receives an
          activation email and lands on a unified dashboard — they manage their building and their
          own employees from the same login.
        </p>
        {successMessage ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-[var(--status-green)]/30 bg-[var(--status-green-tint)] p-4 text-sm text-[var(--text-primary)]">
              {successMessage}
            </div>
            <DialogFooter>
              <Button onClick={() => handleClose(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit((data) => {
              setSubmitError('');
              mutation.mutate({
                fullName: data.fullName,
                email: data.email,
                companyName: data.companyName,
                buildingAddress: data.buildingAddress,
                ...(data.subdomain ? { subdomain: data.subdomain } : {}),
              });
            })}
            className="space-y-4"
          >
            <div className="space-y-1">
              <Label htmlFor="invite-full-name">Customer full name</Label>
              <Input id="invite-full-name" {...register('fullName')} placeholder="Jane Mwangi" />
              {errors.fullName && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.fullName.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="invite-email">Work email</Label>
              <Input
                id="invite-email"
                type="email"
                {...register('email')}
                placeholder="jane@acme.co.ke"
              />
              {errors.email && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="invite-company">Company name</Label>
              <Input
                id="invite-company"
                {...register('companyName')}
                placeholder="Acme Corporation Kenya Ltd"
              />
              {errors.companyName && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.companyName.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="invite-address">Building address</Label>
              <Input
                id="invite-address"
                {...register('buildingAddress')}
                placeholder="Westlands Tower, Waiyaki Way, Nairobi"
              />
              {errors.buildingAddress && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.buildingAddress.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="invite-subdomain">
                Subdomain <span className="text-[var(--text-tertiary)]">(optional)</span>
              </Label>
              <Input
                id="invite-subdomain"
                {...register('subdomain')}
                placeholder="acme — leave blank to auto-generate"
              />
              {errors.subdomain && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.subdomain.message}</p>
              )}
            </div>
            {submitError && <p className="text-xs text-[var(--status-red-text)]">{submitError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Sending invitation…' : 'Send Activation Email'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Org Detail / Edit ────────────────────────────────────────────────────────

const editOrgSchema = z.object({
  name: z.string().min(2, 'Name required'),
  contactName: z.string().min(2, 'Contact name required'),
  contactEmail: z.string().email('Valid email required'),
  type: z.enum(['estate_management', 'security_company']),
});
type EditOrgForm = z.infer<typeof editOrgSchema>;

function OrgDetailDialog({ org, onClose }: { org: Organisation | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [editError, setEditError] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<EditOrgForm>({ resolver: zodResolver(editOrgSchema) });

  useEffect(() => {
    if (org) {
      reset({
        name: org.name,
        contactName: org.contactName,
        contactEmail: org.contactEmail,
        type: org.type as EditOrgForm['type'],
      });
      setResendMessage('');
    }
  }, [org, reset]);

  const mutation = useMutation({
    mutationFn: (data: EditOrgForm) => updateOrganisation(org?.id ?? '', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'organisations'] });
      onClose();
    },
    onError: (err: unknown) =>
      setEditError(extractApiErrorMessage(err, 'Failed to update. Please try again.')),
  });

  const resendMutation = useMutation({
    mutationFn: () => resendActivation(org?.id ?? ''),
    onSuccess: (result) => {
      setResendMessage(
        `Activation email resent to ${result.email}. Expires: ${new Date(result.expiresAt).toLocaleDateString()}.`,
      );
    },
    onError: (err: unknown) => {
      setResendMessage(extractApiErrorMessage(err, 'Failed to resend activation email.'));
    },
  });

  if (!org) return null;

  return (
    <Dialog
      open={!!org}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Estate / Customer Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-[var(--text-secondary)]">Status</p>
              <Badge
                variant={org.status === 'active' ? 'default' : 'destructive'}
                className="mt-1 text-xs"
              >
                {org.status}
              </Badge>
            </div>
            <div>
              <p className="text-[var(--text-secondary)]">Properties</p>
              <p className="font-medium text-[var(--text-primary)] mt-1">{org.propertiesCount}</p>
            </div>
            <div>
              <p className="text-[var(--text-secondary)]">Users</p>
              <p className="font-medium text-[var(--text-primary)] mt-1">{org.usersCount}</p>
            </div>
            <div>
              <p className="text-[var(--text-secondary)]">Created</p>
              <p className="font-medium text-[var(--text-primary)] mt-1">
                {new Date(org.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Resend activation section */}
          <div className="rounded-lg border border-[var(--border)] p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">Activation Email</p>
                <p className="text-xs text-[var(--text-secondary)]">
                  Resend the activation invitation to {org.contactEmail}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={resendMutation.isPending}
                onClick={() => {
                  setResendMessage('');
                  resendMutation.mutate();
                }}
              >
                <Send className="size-3.5 mr-1.5" />
                {resendMutation.isPending ? 'Sending…' : 'Resend'}
              </Button>
            </div>
            {resendMessage && (
              <p
                className={`text-xs ${resendMutation.isError ? 'text-[var(--status-red-text)]' : 'text-[var(--status-green-text)]'}`}
              >
                {resendMessage}
              </p>
            )}
          </div>

          <form
            id="edit-org-form"
            onSubmit={handleSubmit((data) => {
              setEditError('');
              mutation.mutate(data);
            })}
            className="space-y-3"
          >
            <div className="space-y-1">
              <Label htmlFor="edit-org-name">Name</Label>
              <Input id="edit-org-name" {...register('name')} />
              {errors.name && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.name.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-org-type">Type</Label>
              <select
                id="edit-org-type"
                {...register('type')}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="estate_management">Estate Management</option>
                <option value="security_company">Security Company</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-contact-name">Contact Name</Label>
              <Input id="edit-contact-name" {...register('contactName')} />
              {errors.contactName && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.contactName.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-contact-email">Contact Email</Label>
              <Input id="edit-contact-email" type="email" {...register('contactEmail')} />
              {errors.contactEmail && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.contactEmail.message}</p>
              )}
            </div>
            {editError && <p className="text-xs text-[var(--status-red-text)]">{editError}</p>}
          </form>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button type="submit" form="edit-org-form" disabled={!isDirty || mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Invite Estate (residential estate, ADR 010 slice 2) ─────────────────────

const inviteEstateSchema = z.object({
  fullName: z.string().min(2, 'Contact name required'),
  email: z.string().email('Valid email required'),
  estateName: z.string().min(2, 'Estate name required'),
  estateAddress: z.string().min(2, 'Estate address required'),
  propertyType: z.enum([
    'gated_community',
    'residential_apartment',
    'master_planned_city',
    'mixed_use',
    'other',
  ]),
  subdomain: z
    .string()
    .regex(
      /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/,
      'Lowercase letters, digits, hyphens; 3–40 chars',
    )
    .optional()
    .or(z.literal('')),
});
type InviteEstateForm = z.infer<typeof inviteEstateSchema>;

const ESTATE_TYPES = [
  { value: 'gated_community', label: 'Gated Community' },
  { value: 'residential_apartment', label: 'Apartment Complex' },
  { value: 'master_planned_city', label: 'Master-Planned City' },
  { value: 'mixed_use', label: 'Mixed-Use' },
  { value: 'other', label: 'Other' },
] as const;

function InviteEstateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteEstateForm>({
    resolver: zodResolver(inviteEstateSchema),
    defaultValues: { propertyType: 'gated_community' },
  });
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const mutation = useMutation({
    mutationFn: provisionEstateCustomer,
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'organisations'] });
      setSuccessMessage(
        `Activation email sent to ${result.email}. Subdomain: ${result.subdomain}.`,
      );
      reset();
    },
    onError: (err: unknown) => {
      setSubmitError(extractApiErrorMessage(err, 'Failed to invite estate. Please try again.'));
    },
  });

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      reset();
      setSubmitError('');
      setSuccessMessage('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Invite a Residential Estate</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[var(--text-secondary)]">
          Stand up a self-managed residential estate in one step. The property manager receives an
          activation email and lands on the PM dashboard ready to invite residents.
        </p>
        {successMessage ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-[var(--status-green)]/30 bg-[var(--status-green-tint)] p-4 text-sm text-[var(--text-primary)]">
              {successMessage}
            </div>
            <DialogFooter>
              <Button onClick={() => handleClose(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit((data) => {
              setSubmitError('');
              mutation.mutate({
                fullName: data.fullName,
                email: data.email,
                estateName: data.estateName,
                estateAddress: data.estateAddress,
                propertyType: data.propertyType,
                ...(data.subdomain ? { subdomain: data.subdomain } : {}),
              });
            })}
            className="space-y-4"
          >
            <div className="space-y-1">
              <Label htmlFor="estate-full-name">Property manager full name</Label>
              <Input id="estate-full-name" {...register('fullName')} placeholder="John Kamau" />
              {errors.fullName && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.fullName.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="estate-email">Work email</Label>
              <Input
                id="estate-email"
                type="email"
                {...register('email')}
                placeholder="john@runda.co.ke"
              />
              {errors.email && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="estate-name">Estate name</Label>
              <Input id="estate-name" {...register('estateName')} placeholder="Runda Estate" />
              {errors.estateName && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.estateName.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="estate-address">Estate address</Label>
              <Input
                id="estate-address"
                {...register('estateAddress')}
                placeholder="Runda, Nairobi, Kenya"
              />
              {errors.estateAddress && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.estateAddress.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="estate-type">Property type</Label>
              <select
                id="estate-type"
                {...register('propertyType')}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {ESTATE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="estate-subdomain">
                Subdomain <span className="text-[var(--text-tertiary)]">(optional)</span>
              </Label>
              <Input
                id="estate-subdomain"
                {...register('subdomain')}
                placeholder="runda — leave blank to auto-generate"
              />
              {errors.subdomain && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.subdomain.message}</p>
              )}
            </div>
            {submitError && <p className="text-xs text-[var(--status-red-text)]">{submitError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Sending invitation…' : 'Send Activation Email'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Decommission Property Confirmation ────────────────────────────────────────

function DecommissionPropertyDialog({
  property,
  onClose,
}: {
  property: OrgProperty | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [confirmValue, setConfirmValue] = useState('');
  const [actionError, setActionError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const mutation = useMutation({
    mutationFn: (name: string) => decommissionProperty(property?.id ?? '', name),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'organisations'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'org-properties'] });
      const msg = result.orgAlsoDecommissioned
        ? `Property decommissioned. The parent organisation was also decommissioned (it had no remaining active properties).`
        : `Property "${result.propertyName}" has been decommissioned.`;
      setSuccessMessage(msg);
      setConfirmValue('');
    },
    onError: (err: unknown) =>
      setActionError(extractApiErrorMessage(err, 'Failed to decommission. Please try again.')),
  });

  if (!property) return null;

  return (
    <Dialog
      open={!!property}
      onOpenChange={() => {
        setConfirmValue('');
        setActionError('');
        setSuccessMessage('');
        onClose();
      }}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Decommission Property</DialogTitle>
        </DialogHeader>
        {successMessage ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-[var(--status-green)]/30 bg-[var(--status-green-tint)] p-4 text-sm text-[var(--text-primary)]">
              {successMessage}
            </div>
            <DialogFooter>
              <Button onClick={onClose}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-[var(--text-secondary)]">
              This will decommission <strong>{property.name}</strong>. All active user sessions for
              this property will be invalidated. The property data is preserved but it will no
              longer be accessible. This action is reversible.
            </p>
            <p className="text-sm text-[var(--text-secondary)]">
              Type the property name to confirm.
            </p>
            <div className="space-y-1">
              <Label htmlFor="confirm-property-name">Confirm property name</Label>
              <Input
                id="confirm-property-name"
                value={confirmValue}
                onChange={(e) => setConfirmValue(e.target.value)}
                placeholder={property.name}
              />
            </div>
            {actionError && <p className="text-xs text-[var(--status-red-text)]">{actionError}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={confirmValue !== property.name || mutation.isPending}
                onClick={() => mutation.mutate(confirmValue)}
              >
                {mutation.isPending ? 'Decommissioning…' : 'Decommission Property'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Expandable Properties Row ─────────────────────────────────────────────────

function AddManagerDialog({
  property,
  onClose,
}: {
  property: OrgProperty | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState('');

  const schema = z.object({
    fullName: z.string().min(2, 'Full name required'),
    email: z.string().email('Valid email required'),
    phone: z.string().optional(),
  });
  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      adminInvitePropertyManager(property?.id ?? '', {
        fullName: values.fullName,
        email: values.email,
        phone: values.phone ?? '',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'organisations'] });
      onClose();
      reset();
      setError('');
    },
    onError: (err: unknown) => {
      setError(extractApiErrorMessage(err, 'Failed to invite manager.'));
    },
  });

  if (!property) return null;

  return (
    <Dialog
      open={!!property}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
          reset();
          setError('');
        }
      }}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Add Property Manager</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[var(--text-secondary)]">
          Invite a new property manager to <strong>{property.name}</strong>. They will receive an
          activation email.
        </p>
        <form
          onSubmit={handleSubmit((data) => {
            setError('');
            mutation.mutate(data);
          })}
          className="space-y-3 mt-2"
        >
          <div className="space-y-1">
            <Label htmlFor="mgr-full-name">Full name</Label>
            <Input id="mgr-full-name" {...register('fullName')} placeholder="Jane Kamau" />
            {errors.fullName && (
              <p className="text-xs text-[var(--status-red-text)]">{errors.fullName.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="mgr-email">Email</Label>
            <Input
              id="mgr-email"
              type="email"
              {...register('email')}
              placeholder="jane@estate.co.ke"
            />
            {errors.email && (
              <p className="text-xs text-[var(--status-red-text)]">{errors.email.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="mgr-phone">Phone (optional)</Label>
            <Input
              id="mgr-phone"
              type="tel"
              {...register('phone')}
              placeholder="+254 700 000 000"
            />
          </div>
          {error && <p className="text-xs text-[var(--status-red-text)]">{error}</p>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onClose();
                reset();
                setError('');
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Sending…' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OrgPropertiesRow({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const [decommissionTarget, setDecommissionTarget] = useState<OrgProperty | null>(null);
  const [addManagerTarget, setAddManagerTarget] = useState<OrgProperty | null>(null);

  const { data: properties = [], isLoading } = useQuery({
    queryKey: ['admin', 'org-properties', orgId],
    queryFn: () => listOrgProperties(orgId),
  });

  const reactivateMutation = useMutation({
    mutationFn: reactivateProperty,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'organisations'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'org-properties', orgId] });
    },
  });

  if (isLoading) {
    return (
      <tr>
        <td colSpan={6} className="px-8 py-3">
          <Skeleton className="h-4 w-full" />
        </td>
      </tr>
    );
  }

  if (properties.length === 0) {
    return (
      <tr>
        <td colSpan={6} className="px-8 py-3 text-sm text-[var(--text-secondary)]">
          No properties found for this organisation.
        </td>
      </tr>
    );
  }

  return (
    <>
      {properties.map((prop) => (
        <tr
          key={prop.id}
          className="border-b border-[var(--bg-surface-raised)] last:border-0 bg-[var(--bg-surface-raised)]/30"
        >
          <td className="px-8 py-2 text-sm">
            <div>
              <p className="font-medium text-[var(--text-primary)]">{prop.name}</p>
              <p className="text-xs text-[var(--text-secondary)]">{prop.address}</p>
            </div>
          </td>
          <td className="px-4 py-2 text-sm text-[var(--text-secondary)] capitalize">
            {prop.propertyType.replace(/_/g, ' ')}
          </td>
          <td className="px-4 py-2 text-sm text-right text-[var(--text-secondary)]">
            {prop.subdomain}
          </td>
          <td className="px-4 py-2 text-sm text-right text-[var(--text-secondary)]">—</td>
          <td className="px-4 py-2">
            <Badge
              variant={
                prop.status === 'active'
                  ? 'default'
                  : prop.status === 'decommissioned'
                    ? 'secondary'
                    : 'destructive'
              }
              className="text-xs"
            >
              {prop.status}
            </Badge>
          </td>
          <td className="px-4 py-2">
            <div className="flex items-center gap-1">
              {prop.status === 'active' && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setAddManagerTarget(prop)}>
                    Add Manager
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDecommissionTarget(prop)}
                  >
                    Decommission
                  </Button>
                </>
              )}
              {prop.status === 'decommissioned' && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={reactivateMutation.isPending}
                  onClick={() => reactivateMutation.mutate(prop.id)}
                >
                  Reactivate
                </Button>
              )}
            </div>
          </td>
        </tr>
      ))}
      <DecommissionPropertyDialog
        property={decommissionTarget}
        onClose={() => setDecommissionTarget(null)}
      />
      <AddManagerDialog property={addManagerTarget} onClose={() => setAddManagerTarget(null)} />
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function SuperAdminOrgsPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEstateOpen, setInviteEstateOpen] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState<Organisation | null>(null);
  const [purgeTarget, setPurgeTarget] = useState<Organisation | null>(null);
  const [viewTarget, setViewTarget] = useState<Organisation | null>(null);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const qc = useQueryClient();

  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ['admin', 'organisations'],
    queryFn: getOrganisations,
  });

  const reinstateMutation = useMutation({
    mutationFn: reinstateOrganisation,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'organisations'] }),
  });

  const toggleExpand = (orgId: string) => {
    setExpandedOrgs((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl text-[var(--text-primary)]">Customers</h1>
          <p className="text-sm text-[var(--text-secondary)]">Manage all tenant customers</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setInviteEstateOpen(true)}>
            <Send className="size-4 mr-2" />
            Invite Estate
          </Button>
          <Button variant="outline" onClick={() => setInviteOpen(true)}>
            <Send className="size-4 mr-2" />
            Invite Customer
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4 mr-2" />
            Create Customer
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--bg-surface-raised)] text-[var(--text-secondary)]">
              <th className="text-left px-4 py-3 font-medium">Customer</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-right px-4 py-3 font-medium">Properties</th>
              <th className="text-right px-4 py-3 font-medium">Users</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? [1, 2, 3].map((i) => (
                  <tr key={i}>
                    <td className="px-4 py-3" colSpan={6}>
                      <Skeleton className="h-4 w-full" />
                    </td>
                  </tr>
                ))
              : orgs.map((org) => {
                  const isExpanded = expandedOrgs.has(org.id);
                  return (
                    <React.Fragment key={org.id}>
                      <tr className="border-b border-[var(--bg-surface-raised)] last:border-0">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => toggleExpand(org.id)}
                              className="p-0.5 rounded hover:bg-[var(--bg-surface-raised)] text-[var(--text-secondary)]"
                              aria-label={isExpanded ? 'Collapse properties' : 'Expand properties'}
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? (
                                <ChevronDown className="size-4" />
                              ) : (
                                <ChevronRight className="size-4" />
                              )}
                            </button>
                            <Building2 className="size-4 text-[var(--text-secondary)] shrink-0" />
                            <div>
                              <p className="font-medium text-[var(--text-primary)]">{org.name}</p>
                              <p className="text-xs text-[var(--text-secondary)]">
                                {org.contactEmail}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[var(--text-secondary)] capitalize">
                          {org.type}
                        </td>
                        <td className="px-4 py-3 text-right text-[var(--text-secondary)]">
                          {org.propertiesCount}
                        </td>
                        <td className="px-4 py-3 text-right text-[var(--text-secondary)]">
                          {org.usersCount}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={org.status === 'active' ? 'default' : 'destructive'}
                            className="text-xs"
                          >
                            {org.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setViewTarget(org)}>
                              View
                            </Button>
                            {org.status === 'active' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => setSuspendTarget(org)}
                              >
                                Suspend
                              </Button>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={reinstateMutation.isPending}
                                  onClick={() => reinstateMutation.mutate(org.id)}
                                >
                                  Reinstate
                                </Button>
                                {org.status === 'suspended' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => setPurgeTarget(org)}
                                    title="Permanently delete this customer"
                                  >
                                    <Trash2 className="size-3.5" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && <OrgPropertiesRow orgId={org.id} />}
                    </React.Fragment>
                  );
                })}
          </tbody>
        </table>
      </div>

      <CreateOrgDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <InviteCustomerDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <InviteEstateDialog open={inviteEstateOpen} onClose={() => setInviteEstateOpen(false)} />
      <SuspendConfirmDialog org={suspendTarget} onClose={() => setSuspendTarget(null)} />
      <PurgeConfirmDialog org={purgeTarget} onClose={() => setPurgeTarget(null)} />
      <OrgDetailDialog org={viewTarget} onClose={() => setViewTarget(null)} />
    </div>
  );
}
